import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listsApi } from '@/lib/api';
import type { List, TaskStatus } from '@/types';

export function useLists(projectId: string | undefined, includeArchived = false) {
  return useQuery({
    queryKey: ['lists', projectId, includeArchived],
    queryFn: async () => {
      if (!projectId) return [];
      const response = await listsApi.getByProject(
        projectId,
        includeArchived ? { includeArchived: 'true' } : undefined
      );
      return response.data as List[];
    },
    enabled: !!projectId,
  });
}

export function useCreateList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      name,
    }: {
      projectId: string;
      name: string;
    }) => {
      const response = await listsApi.create(projectId, { name });
      return response.data as List;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lists', variables.projectId] });
    },
  });
}

export function useUpdateList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      name,
    }: {
      id: string;
      name: string;
      projectId: string;
    }) => {
      const response = await listsApi.update(id, { name });
      return response.data as List;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lists', variables.projectId] });
    },
  });
}

export function useDeleteList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
    }: {
      id: string;
      projectId: string;
    }) => {
      await listsApi.delete(id);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lists', variables.projectId] });
    },
  });
}

export function useArchiveList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: string; projectId: string }) => {
      const res = await listsApi.archive(id);
      return res.data as List;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lists', variables.projectId] });
    },
  });
}

export function useUnarchiveList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: string; projectId: string }) => {
      const res = await listsApi.unarchive(id);
      return res.data as List;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lists', variables.projectId] });
    },
  });
}

// ============================================
// LIST STATUS MANAGEMENT HOOKS
// ============================================

/**
 * Get effective statuses for a list
 * If list uses custom statuses, returns list-specific statuses
 * Otherwise, returns project-level statuses
 */
export function useListStatuses(listId: string | undefined) {
  return useQuery({
    queryKey: ['listStatuses', listId],
    queryFn: async () => {
      if (!listId) return [];
      const response = await listsApi.getStatuses(listId);
      return response.data as TaskStatus[];
    },
    enabled: !!listId,
  });
}

/**
 * Enable custom statuses for a list
 * This copies project statuses to the list as a starting point
 */
export function useEnableCustomStatuses() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      listId,
    }: {
      listId: string;
      projectId: string;
    }) => {
      const response = await listsApi.enableCustomStatuses(listId);
      return response.data as List;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lists', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['listStatuses', variables.listId] });
    },
  });
}

/**
 * Disable custom statuses for a list
 * Reverts to using project-level statuses
 */
export function useDisableCustomStatuses() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      listId,
    }: {
      listId: string;
      projectId: string;
    }) => {
      const response = await listsApi.disableCustomStatuses(listId);
      return response.data as List;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lists', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['listStatuses', variables.listId] });
    },
  });
}

/**
 * Create a custom status for a list (only if list uses custom statuses)
 */
export function useCreateListStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      listId,
      name,
      color,
      isDoneState,
    }: {
      listId: string;
      projectId: string;
      name: string;
      color?: string;
      isDoneState?: boolean;
    }) => {
      const response = await listsApi.createStatus(listId, { name, color, isDoneState });
      return response.data as TaskStatus;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['listStatuses', variables.listId] });
    },
  });
}

/**
 * Update a custom status for a list
 */
export function useUpdateListStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      listId,
      statusId,
      name,
      color,
      isDoneState,
    }: {
      listId: string;
      statusId: string;
      name?: string;
      color?: string;
      isDoneState?: boolean;
    }) => {
      const response = await listsApi.updateStatus(listId, statusId, { name, color, isDoneState });
      return response.data as TaskStatus;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['listStatuses', variables.listId] });
    },
  });
}

/**
 * Delete a custom status for a list
 */
export function useDeleteListStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      listId,
      statusId,
      replacementStatusId,
    }: {
      listId: string;
      projectId: string;
      statusId: string;
      replacementStatusId?: string;
    }) => {
      await listsApi.deleteStatus(listId, statusId, replacementStatusId);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['listStatuses', variables.listId] });
      queryClient.invalidateQueries({ queryKey: ['tasks', variables.projectId] });
    },
  });
}

/**
 * Reorder custom statuses for a list
 */
export function useReorderListStatuses() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      listId,
      statusIds,
    }: {
      listId: string;
      statusIds: string[];
    }) => {
      const response = await listsApi.reorderStatuses(listId, statusIds);
      return response.data as TaskStatus[];
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['listStatuses', variables.listId] });
    },
  });
}
